'use client';

import { SEARCH_TYPE } from '@/constants/searchType';
import { useParams } from 'next/navigation';
import {
  FieldOption,
  GetAnimalsRow,
  GetSaleLotsRow,
  GetSalesRow,
} from '../constants';
import { Box, Grid, Skeleton, Stack } from '@/primitives';
import { Card, CardContent } from '@mui/material';
import { alpha, Theme } from '@mui/material/styles';
import AnimalCard from './AnimalCard';
import useGridStore from '@/publicSearch/store/useGridStore';
import { useSearch } from '@/hooks/publicSearch/useSearch';
import { useSearchType } from '@/hooks/publicSearch/useSearchType';
import SaleCatalogueDetailAnimalCard from './SaleCatalogueDetailAnimalCard';
import GalleryCard from '../sale-catalogues/GalleryCard';
import MemberCard from './MemberCard';
import { ROUTE_SEGMENT } from '@/constants/routeSegments';
import { Pagination } from '@/primitives/Pagination';

const cardSkeletonSx = {
  padding: '16px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  overflow: 'hidden',
  boxShadow: (t: Theme) => `0 0 8px ${alpha(t.palette.common.black, 0.1)}`,
  border: '1px solid',
  borderColor: 'divider',
  '@media (max-width: 599.95px)': {
    padding: '12px 16px',
    borderRadius: 0,
    boxShadow: 'none',
    border: 'none',
    borderBottom: '1px solid',
    borderBottomColor: 'divider',
  },
};

// Rounds the outer corners of the first/last card so the mobile single-column
// list reads as one rounded block. Returns undefined for interior cards.
function gridCornerSx(isFirst: boolean, isLast: boolean) {
  return isFirst || isLast
    ? (theme: Theme) => ({
        [theme.breakpoints.down('sm')]: {
          '& .MuiPaper-root': {
            ...(isFirst && {
              borderTopLeftRadius: '8px',
              borderTopRightRadius: '8px',
            }),
            ...(isLast && {
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
            }),
          },
        },
      })
    : undefined;
}

// Skeleton matching AnimalCard / MemberCard / SaleCatalogueDetailAnimalCard layout
// (status badge, name + image, 4-cell field grid). When `withFooters` is set, three
// footer rows are appended to match the AnimalCard / MemberCard layout.
function AnimalCardSkeleton({
  withFooters = false,
}: {
  withFooters?: boolean;
}) {
  return (
    <Card sx={cardSkeletonSx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Box
            sx={{
              height: '24px',
              mb: '8px',
            }}
          >
            <Skeleton variant="rounded" width={80} height={24} />
          </Box>
          <Box
            sx={{
              height: '48px',
              mb: '8px',
            }}
          >
            <Skeleton variant="text" width="80%" height={32} />
          </Box>
        </Box>
        <Skeleton
          variant="rounded"
          width={56}
          height={56}
          sx={{ borderRadius: '4px', flexShrink: 0 }}
        />
      </Box>
      <CardContent
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          columnGap: '16px',
          rowGap: '4px',
          padding: 0,
        }}
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 'calc(50% - 8px)',
              height: '44px',
            }}
          >
            <Skeleton
              variant="text"
              width="60%"
              height={16}
              sx={{ mb: '4px' }}
            />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        ))}
      </CardContent>
      {withFooters &&
        Array.from({ length: 3 }).map((_, index) => (
          <Box
            key={`footer-${index}`}
            sx={{
              mt: '4px',
              height: '44px',
            }}
          >
            <Skeleton
              variant="text"
              width="40%"
              height={14}
              sx={{ mb: '4px' }}
            />
            <Skeleton variant="text" width="70%" height={20} />
          </Box>
        ))}
    </Card>
  );
}

// Skeleton matching AnimalCard / MemberCard layout (status badge, name, optional image, field grid, footers)
function CardSkeleton() {
  return <AnimalCardSkeleton withFooters />;
}

// Skeleton matching SaleCatalogueDetailAnimalCard layout (status badge, name + image, 4-cell grid, no footers)
function SaleCatalogueDetailCardSkeleton() {
  return <AnimalCardSkeleton />;
}

// Skeleton matching GalleryCard layout (sale type badge, sale name, date + lots grid — no image)
function SaleCatalogueCardSkeleton() {
  return (
    <Card sx={cardSkeletonSx}>
      <Box
        sx={{
          mb: '12px',
        }}
      >
        <Box
          sx={{
            height: '24px',
            mb: '4px',
          }}
        >
          <Skeleton variant="rounded" width={72} height={20} />
        </Box>
        <Skeleton variant="text" width="90%" height={28} />
        <Skeleton variant="text" width="65%" height={28} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          columnGap: '16px',
          mt: '8px',
        }}
      >
        {Array.from({ length: 2 }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 'calc(50% - 8px)',
              height: '44px',
            }}
          >
            <Skeleton
              variant="text"
              width="60%"
              height={16}
              sx={{ mb: '4px' }}
            />
            <Skeleton variant="text" width="80%" height={20} />
          </Box>
        ))}
      </Box>
    </Card>
  );
}

export default function PublicSearchGalleryView() {
  const params = useParams<{ orgName?: string }>();
  const orgCode = params.orgName ?? '';
  const { paginationModel, setPaginationModel } = useGridStore();
  const { data, dataGrid, loading } = useSearch();
  const searchType = useSearchType();
  const isAnimal = searchType === SEARCH_TYPE.ANIMAL;
  const isSaleCatalogueDetail =
    searchType === SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS ||
    searchType === SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS;
  const isSaleCatalogue = searchType === SEARCH_TYPE.SALE_CATALOGUES;
  const isMember = searchType === SEARCH_TYPE.MEMBERS;
  const rowCount = data?.totalCount || 0;
  const pageCount = Math.ceil(rowCount / paginationModel.pageSize);

  const getMemberData = (row: any): any => {
    const memberId = row.memberId ?? row[FieldOption.memberId.value];
    const detailPath =
      orgCode && memberId
        ? `/${orgCode}/${ROUTE_SEGMENT.MEMBERS}/${memberId}`
        : undefined;
    return {
      name: row[FieldOption.memberName.value],
      status: row.membStatus,
      herdPrefix: row[FieldOption.herdPrefix.value],
      membershipType: row[FieldOption.membershipType.value],
      memberIdent: row[FieldOption.memberIdent.value],
      herdLetters: row['SORTNAME'] ?? '',
      postcode: row['POSTCODE'] ?? '',
      memberId,
      detailPath,
      documents: row.documents,
    };
  };

  return (
    <Stack
      sx={{
        flexDirection: 'column',
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          px: { xs: 0, sm: '20px' },
          py: { xs: 0, sm: '20px' },
        }}
      >
        {loading && (
          // Show card skeletons when loading
          <Grid container spacing={{ xs: 0, sm: 2 }}>
            {Array.from({ length: 12 }).map((_, index) => {
              const isFirst = index === 0;
              const isLast = index === 11;
              return (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  key={`skeleton-${index}`}
                  sx={gridCornerSx(isFirst, isLast)}
                >
                  {(() => {
                    if (isSaleCatalogue) return <SaleCatalogueCardSkeleton />;
                    if (isSaleCatalogueDetail)
                      return <SaleCatalogueDetailCardSkeleton />;
                    return <CardSkeleton />;
                  })()}
                </Grid>
              );
            })}
          </Grid>
        )}
        {!loading && dataGrid.rows.length > 0 && (
          // Show actual cards when loaded
          <Grid container spacing={{ xs: 0, sm: 2 }}>
            {(dataGrid.rows as any[]).map((row, index) => {
              const isFirst = index === 0;
              const isLast = index === dataGrid.rows.length - 1;
              return (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  key={`${index}:${row.ID ?? row.memberId ?? row[FieldOption.memberId.value] ?? index}`}
                  sx={gridCornerSx(isFirst, isLast)}
                >
                  {isAnimal && <AnimalCard {...(row as GetAnimalsRow)} />}
                  {isSaleCatalogue && <GalleryCard {...(row as GetSalesRow)} />}
                  {isSaleCatalogueDetail && (
                    <SaleCatalogueDetailAnimalCard
                      {...(row as GetSaleLotsRow)}
                    />
                  )}
                  {isMember && <MemberCard data={getMemberData(row)} />}
                </Grid>
              );
            })}
          </Grid>
        )}
      </Box>
      {!loading && (
        <Box sx={{ flexShrink: 0, display: 'flex', pt: { xs: 0.5, sm: 1.5 } }}>
          <Pagination
            page={paginationModel.page}
            pageCount={pageCount}
            pageSize={paginationModel.pageSize}
            rowCount={rowCount}
            onPageChange={(page) =>
              setPaginationModel({ ...paginationModel, page })
            }
            onPageSizeChange={(pageSize) =>
              setPaginationModel({ page: 0, pageSize })
            }
          />
        </Box>
      )}
    </Stack>
  );
}
